import { TrayService } from './tray/tray.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { EventEmitter, NgZone, Renderer2 } from '@angular/core';
import { DraggableModalOptions, DraggableModalService } from './draggable-modal.service';

export class DraggableModal {
  modalRef: BsModalRef;
  modalEl: Element;
  modalBody: HTMLElement;
  mouseDownX: number;
  mouseDownY: number;
  width: number;
  height: number;
  id: string;
  trayTitle: string;
  mouseUpUnsubscribe: Function;
  mouseMoveUnsubscribe: Function;
  onHidden: Function;
  posChangeX = 0;
  posChangeY = 0;
  currentPosX = 0;
  currentPosY = 0;
  constructor(private modalService: BsModalService,
              private draggableModalService: DraggableModalService,
              private renderer: Renderer2,
              private trayService: TrayService,
              private zone: NgZone) {}

  openModal(component: Function, options: DraggableModalOptions): BsModalRef {
    this.modalRef = this.modalService.show(component, {
      backdrop: false,
      animated: options.animated || false,
      class: `${this.id} ${options.class || ''}`,
      ignoreBackdropClick: true,
      keyboard: false,
      initialState: Object.assign({
        _minimize: new EventEmitter(),
        _hide: new EventEmitter(),
        minimize: function() {this._minimize.emit()},
        hide: function () {this._hide.emit()},
        _options: options
      }, options.initialState || {})
    });
    this.draggableModalService.modalOpened.emit(this);
    this.modalEl = document.querySelector(`.modal-dialog.${this.id}`);
    this.modalBody = this.modalEl.querySelector('.modal-body');
    const handle = document.querySelector(`.modal-dialog.${this.id} .draggable-modal-handle`);
    this.renderer.setStyle(this.modalEl.parentElement, 'height', '0');
    this.renderer.setStyle(this.modalEl.parentElement, 'overflow', 'visible');
    this.renderer.setStyle(this.modalEl, 'transition', 'none');
    this.renderer.setStyle(this.modalEl, 'margin', '0');
    this.width = options.width || this.modalEl.scrollWidth;
    this.height = options.height || this.modalBody.scrollHeight;
    this.renderer.setStyle(this.modalBody, 'height', `${options.height}px`);
    this.renderer.setStyle(this.modalEl, 'max-width', 'none');
    this.renderer.setStyle(this.modalEl, 'width', `${this.width}px`);
    const centerX = (window.innerWidth / 2) - this.modalEl.scrollWidth / 2;
    this.currentPosX = options.x || centerX || 0;
    this.currentPosY = options.y || 30 || 0;
    this.renderer.setStyle(this.modalEl, 'transform', `translate(${this.currentPosX}px, ${this.currentPosY}px)`);
    this.modalRef.content._minimize.subscribe(() => {
      this.trayService.addModalToTray(Object.assign(this.modalRef.content._options, {
        x: this.currentPosX,
        y: this.currentPosY,
        width: this.width,
        height: this.modalBody.scrollHeight
      }));
      this.renderer.setStyle(this.modalEl.parentElement, 'display', 'none');
    });
    this.modalRef.content._hide.subscribe(() => {
      this.modalEl.parentElement.innerHTML = ' ';
      this.modalRef.hide();
      this.modalRef = null;
      this.modalEl = null;
      if (this.onHidden) {
        this.onHidden();
      }
    });
    this.zone.runOutsideAngular(() => {
      const resizableArea = this.modalEl.querySelector('.resizable-area');
      if (resizableArea) {
        this.renderer.setStyle(resizableArea, 'right', `${this.modalBody.offsetWidth - this.modalBody.clientWidth}px`);
        this.renderer.listen(resizableArea, 'mousedown', (e: MouseEvent) => {
          if (e.button !== 0) {
            return;
          }
          this.renderer.addClass(this.modalEl, 'unselectable');
          this.renderer.setStyle(this.modalBody, 'pointer-events', 'none');
          this.renderer.setStyle(this.modalEl, 'cursor', 'nwse-resize');
          const width = this.modalEl.scrollWidth;
          const heigth = this.modalEl.scrollHeight;
          const modalBodyHeight = this.modalBody.clientHeight;
          const originalX = e.clientX;
          const originalY = e.clientY;
          const resizeMouseMoveUnsubscribe = this.renderer.listen('document', 'mousemove', (mouseMoveEvent: MouseEvent) => {
            if (this.modalRef.content.onModalResize) {
              this.modalRef.content.onModalResize((mouseMoveEvent.clientX - originalX), (mouseMoveEvent.clientY - originalY));
            }
            this.height = modalBodyHeight + (mouseMoveEvent.clientY - originalY);
            this.height = this.height > (options.minHeight || 0) ? this.height : options.minHeight;
            this.renderer.setStyle(this.modalBody, 'height', `${this.height}px`);
            this.width = width + (mouseMoveEvent.clientX - originalX);
            this.width = this.width > (options.minWidth || 0) ? this.width : options.minWidth;
            this.renderer.setStyle(this.modalEl, 'width', `${this.width}px`);
            this.renderer.setStyle(resizableArea, 'right', `${this.modalBody.offsetWidth - this.modalBody.clientWidth}px`);
          });
          const resizeMouseUpUnsubscribe = this.renderer.listen('document', 'mouseup', () => {
            this.renderer.removeStyle(this.modalBody, 'pointer-events');
            this.renderer.removeStyle(this.modalEl, 'cursor');
            resizeMouseMoveUnsubscribe();
            resizeMouseUpUnsubscribe();
          });
        });
      }
      this.renderer.listen(handle || this.modalEl, 'mousedown', (e: MouseEvent) => {
        if (e.button !== 0 ||  (<HTMLElement>e.target).classList.contains('resizable-area')) {
          return;
        }
        this.renderer.addClass(handle || this.modalEl, 'unselectable');
        this.renderer.setStyle(this.modalBody, 'pointer-events', 'none');
        this.setCurrentModalClass();
        this.mouseDownX = e.clientX;
        this.mouseDownY = e.clientY;
        this.posChangeX = 0;
        this.posChangeY = 0;
        this.removeListeners();
        this.mouseMoveUnsubscribe = this.renderer.listen('document', 'mousemove', (mouseMoveEvent: MouseEvent) => {
          let posChangeX = mouseMoveEvent.clientX - this.mouseDownX;
          let posChangeY = mouseMoveEvent.clientY - this.mouseDownY;
          const currentPos = this.modalEl.getBoundingClientRect();
          this.mouseDownX = mouseMoveEvent.clientX;
          this.mouseDownY = mouseMoveEvent.clientY;
          if ((currentPos.top + posChangeY) < 0) {
            this.currentPosY = 0;
            posChangeY = 0;
          }

          if ((currentPos.left + posChangeX) < 0) {
            this.currentPosX = 0;
            posChangeX = 0;
          }

          if ((currentPos.bottom + posChangeY) > window.innerHeight) {
            this.currentPosY = window.innerHeight - this.modalEl.clientHeight;
            posChangeY = 0;
          }

          if ((currentPos.right + posChangeX) > document.body.clientWidth) {
            this.currentPosX = document.body.clientWidth - this.modalEl.clientWidth;
            posChangeX = 0;
          }

          this.posChangeX = posChangeX;
          this.posChangeY = posChangeY;
          this.currentPosX += this.posChangeX;
          this.currentPosY += this.posChangeY;
          this.renderer.setStyle(this.modalEl, 'transform', `translate(${this.currentPosX}px, ${this.currentPosY}px)`);
        });
        this.mouseUpUnsubscribe = this.renderer.listen('document', 'mouseup', () => {
          this.savePosition();
        });
      });
    });
    return this.modalRef;
  }

  savePosition() {
    this.removeListeners();
    this.renderer.removeClass(this.modalEl, 'unselectable');
    this.renderer.removeStyle(this.modalBody, 'pointer-events');
  }

  removeListeners() {
    if (this.mouseMoveUnsubscribe) {
      this.mouseMoveUnsubscribe();
      this.mouseMoveUnsubscribe = null;
    }
    if (this.mouseUpUnsubscribe) {
      this.mouseUpUnsubscribe();
      this.mouseUpUnsubscribe = null;
    }
  }

  setCurrentModalClass() {
    const modals = Array.from(document.querySelectorAll('modal-container'));
    modals.forEach((modal) => {
      this.renderer.removeClass(modal, 'current-modal');
    });
    this.renderer.addClass(this.modalEl.parentElement, 'current-modal');
  }

  showModalFromTray() {
    this.renderer.setStyle(this.modalEl.parentElement, 'display', 'block');
  }
}
